import type { ChainId } from '@morpho-org/blue-sdk'

import { isDefined } from '@morpho-org/morpho-ts'
import { useQuery } from '@tanstack/react-query'
import { getBlock } from 'viem/actions'

import { useWeb3Context } from '@/contexts/Web3Context'

/** Background poll cadence. */
const POLL_INTERVAL_MS = 10_000

/** How long a fetched block stays "fresh" for react-query, gating the extra
 * event-driven refetches (mount / window-focus / reconnect). */
const STALE_TIME_MS = 5_000

export type LatestBlock = {
  number: bigint
  timestamp: bigint
}

/**
 * Poll the latest block number + timestamp for a chain with a direct
 * `eth_getBlockByNumber('latest')` read through the app's RPC transport, so the
 * block-pinned reads that consume it (e.g. vault `getData`) resolve at one block.
 * @param chainId - The chain to read the latest block for; the query is
 *   disabled while it is undefined
 * @param options - `enabled` gates the query (defaults to `true`)
 */
export function useLatestBlock(
  chainId: ChainId | undefined,
  { enabled = true }: { enabled?: boolean } = {}
) {
  const { getClientForChain } = useWeb3Context()

  return useQuery({
    queryKey: ['latestBlock', chainId],
    queryFn: async (): Promise<LatestBlock> => {
      if (!isDefined(chainId)) throw new Error('useLatestBlock: chainId is undefined')
      const { number, timestamp } = await getBlock(getClientForChain(chainId), {
        blockTag: 'latest'
      })
      return { number, timestamp }
    },
    enabled: enabled && isDefined(chainId),
    refetchInterval: POLL_INTERVAL_MS,
    staleTime: STALE_TIME_MS
  })
}
